/**
 * CardPreview Component
 *
 * Dual-platform notification preview with iOS/Android tabs.
 * Each tab shows a realistic notification card for that platform.
 */

import { useState } from "react";
import { Bell, ChevronUp, ChevronDown } from "lucide-react";
import type { ActionButton } from "../types";

interface CardPreviewProps {
  title: string;
  body: string;
  imagePreview?: string | null;
  actionButtons?: ActionButton[];
  appName?: string;
}

type Platform = "ios" | "android";

export function CardPreview({
  title,
  body,
  imagePreview,
  actionButtons = [],
  appName = "Deenex",
}: CardPreviewProps) {
  const [platform, setPlatform] = useState<Platform>("ios");
  const hasContent = title.trim() || body.trim();

  // Filter out empty buttons
  const validButtons = actionButtons.filter((b) => b.label.trim());

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-6">
      {/* Header + Tabs */}
      <div className="flex items-center gap-2 mb-4">
        <Bell size={18} className="text-violet-500" />
        <h3 className="font-semibold text-slate-900">Vista Previa</h3>
        <div className="ml-auto flex gap-1 bg-slate-100 p-0.5 rounded-lg">
          <button
            onClick={() => setPlatform("ios")}
            className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all duration-200 ${
              platform === "ios"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            iOS
          </button>
          <button
            onClick={() => setPlatform("android")}
            className={`px-3 py-1 rounded-md text-[11px] font-semibold transition-all duration-200 ${
              platform === "android"
                ? "bg-white text-slate-900 shadow-sm"
                : "text-slate-500 hover:text-slate-700"
            }`}
          >
            Android
          </button>
        </div>
      </div>

      {/* Preview Area */}
      {platform === "ios" ? (
        <IOSPreview
          title={title}
          body={body}
          imagePreview={imagePreview}
          actionButtons={validButtons}
          appName={appName}
          hasContent={hasContent}
        />
      ) : (
        <AndroidPreview
          title={title}
          body={body}
          imagePreview={imagePreview}
          actionButtons={validButtons}
          appName={appName}
          hasContent={hasContent}
        />
      )}
    </div>
  );
}

/* ─── iOS Notification ─── */
function IOSPreview({
  title,
  body,
  imagePreview,
  actionButtons,
  appName,
  hasContent,
}: {
  title: string;
  body: string;
  imagePreview?: string | null;
  actionButtons: ActionButton[];
  appName: string;
  hasContent: string | boolean;
}) {
  return (
    <div
      className="rounded-2xl p-4 min-h-[200px] flex items-center justify-center relative overflow-hidden"
      style={{
        background:
          "linear-gradient(135deg, #1a1a2e 0%, #16213e 30%, #0f3460 60%, #533483 100%)",
      }}
    >
      {/* Wallpaper light effects */}
      <div
        className="absolute inset-0 opacity-30"
        style={{
          background:
            "radial-gradient(ellipse at 70% 20%, rgba(147,51,234,0.4) 0%, transparent 60%), radial-gradient(ellipse at 30% 80%, rgba(59,130,246,0.3) 0%, transparent 50%)",
        }}
      />

      {hasContent ? (
        <div
          className="w-full max-w-[340px] relative z-10 transition-all duration-500 animate-fade-in"
          key={`ios-${title.length}-${body.length}`}
        >
          <div
            className="rounded-[20px] overflow-hidden"
            style={{
              background: "rgba(255,255,255,0.18)",
              backdropFilter: "blur(40px)",
              WebkitBackdropFilter: "blur(40px)",
              border: "1px solid rgba(255,255,255,0.15)",
              boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
            }}
          >
            {/* Header */}
            <div className="flex items-center gap-2 px-3.5 pt-3 pb-1">
              <div className="w-[22px] h-[22px] bg-gradient-to-br from-violet-500 to-purple-600 rounded-md flex items-center justify-center flex-shrink-0">
                <Bell size={11} className="text-white" />
              </div>
              <span className="text-[12px] font-medium text-white/70 uppercase tracking-wide flex-1">
                {appName}
              </span>
              <span className="text-[11px] text-white/50 mr-1">ahora</span>
              <ChevronUp size={14} className="text-white/40" />
            </div>

            {/* Content */}
            <div className="px-3.5 pb-3 pt-0.5">
              <p className="text-[15px] font-bold text-white leading-tight">
                {title || "Título de la notificación"}
              </p>
              {body && (
                <p className="text-[13px] text-white/75 leading-snug mt-0.5 line-clamp-3">
                  {body}
                </p>
              )}
            </div>

            {/* Image */}
            {imagePreview && (
              <div className="px-3 pb-3">
                <img
                  src={imagePreview}
                  alt="Notification"
                  className="w-full h-[140px] object-cover rounded-xl"
                />
              </div>
            )}

            {/* iOS action buttons */}
            {actionButtons.length > 0 && (
              <div className="border-t border-white/10">
                {actionButtons.map((btn, i) => (
                  <div key={i}>
                    {i > 0 && <div className="h-px bg-white/10" />}
                    <button className="w-full py-2.5 text-[13px] font-medium text-blue-400 text-center">
                      {btn.label}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState variant="dark" />
      )}
    </div>
  );
}

/* ─── Android Notification ─── */
function AndroidPreview({
  title,
  body,
  imagePreview,
  actionButtons,
  appName,
  hasContent,
}: {
  title: string;
  body: string;
  imagePreview?: string | null;
  actionButtons: ActionButton[];
  appName: string;
  hasContent: string | boolean;
}) {
  return (
    <div
      className="rounded-2xl p-4 min-h-[200px] flex items-center justify-center relative overflow-hidden"
      style={{
        background:
          "linear-gradient(160deg, #0f0c29 0%, #302b63 50%, #24243e 100%)",
      }}
    >
      {/* Wallpaper light effects */}
      <div
        className="absolute inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(ellipse at 50% 30%, rgba(99,102,241,0.4) 0%, transparent 60%)",
        }}
      />

      {hasContent ? (
        <div
          className="w-full max-w-[340px] relative z-10 transition-all duration-500 animate-fade-in"
          key={`android-${title.length}-${body.length}`}
        >
          {/* Android Material You Card */}
          <div
            className="rounded-2xl overflow-hidden"
            style={{
              background: "rgba(240, 240, 245, 0.95)",
              boxShadow: "0 4px 24px rgba(0,0,0,0.15)",
            }}
          >
            {/* Header: app icon + name + time + expand */}
            <div className="flex items-center gap-2 px-4 pt-3 pb-1">
              <div className="w-[18px] h-[18px] bg-gradient-to-br from-violet-500 to-purple-600 rounded-full flex items-center justify-center flex-shrink-0">
                <Bell size={9} className="text-white" />
              </div>
              <span className="text-[11px] font-medium text-slate-500 flex-1">
                {appName}
              </span>
              <span className="text-[11px] text-slate-400">ahora</span>
              <ChevronDown size={14} className="text-slate-400 ml-0.5" />
            </div>

            {/* Content */}
            <div className="px-4 pb-3 pt-1 flex gap-3">
              <div className="flex-1 min-w-0">
                <p className="text-[14px] font-bold text-slate-900 leading-tight">
                  {title || "Título de la notificación"}
                </p>
                {body && (
                  <p className="text-[13px] text-slate-600 leading-snug mt-0.5 line-clamp-3">
                    {body}
                  </p>
                )}
              </div>

              {/* Android: small thumbnail on the right (when image exists) */}
              {imagePreview && !body && (
                <img
                  src={imagePreview}
                  alt="Thumb"
                  className="w-10 h-10 rounded-lg object-cover flex-shrink-0 mt-0.5"
                />
              )}
            </div>

            {/* Android: large image (expanded style, with body) */}
            {imagePreview && body && (
              <div className="px-4 pb-3">
                <img
                  src={imagePreview}
                  alt="Notification"
                  className="w-full h-[130px] object-cover rounded-xl"
                />
              </div>
            )}

            {/* Android action buttons bar */}
            {actionButtons.length > 0 && (
              <div className="flex border-t border-slate-200/60">
                {actionButtons.map((btn, i) => (
                  <div key={i} className="flex flex-1">
                    {i > 0 && <div className="w-px bg-slate-200/60" />}
                    <button className="flex-1 py-3.5 text-[11px] font-semibold text-violet-600 uppercase tracking-wide">
                      {btn.label}
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : (
        <EmptyState variant="dark" />
      )}
    </div>
  );
}

/* ─── Empty State ─── */
function EmptyState({ variant }: { variant: "dark" | "light" }) {
  return (
    <div className="text-center py-4 relative z-10">
      <div
        className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-3"
        style={{
          background:
            variant === "dark" ? "rgba(255,255,255,0.1)" : "rgba(0,0,0,0.05)",
          border:
            variant === "dark"
              ? "1px solid rgba(255,255,255,0.1)"
              : "1px solid rgba(0,0,0,0.05)",
        }}
      >
        <Bell
          size={22}
          className={variant === "dark" ? "text-white/30" : "text-slate-300"}
        />
      </div>
      <p
        className={`text-sm ${variant === "dark" ? "text-white/40" : "text-slate-400"}`}
      >
        Escribí el título y mensaje para
        <br />
        ver cómo queda la notificación
      </p>
    </div>
  );
}

export default CardPreview;
